/**
 * Seção Arquivos da ficha do funcionário (E10e — integração Convenia):
 * inventário de documentos/holerites (audit log — SEM download no token) +
 * recibos de férias com cópia local baixada (link de download). Somente
 * leitura; a fonte é o sync (domínio documentos_arquivos).
 */
import { useEffect, useState } from "react";
import { ActionIcon, Badge, Card, Table, Text, Title, Tooltip } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { api, urlComToken } from "../../api";
import { fmtCompetencia } from "../../format";
import { Carregando, Vazio } from "../../componentes/Estados";
import { TabelaDados, Th } from "../../componentes/listas";

interface Arquivo {
  id: string;
  tipo: string;
  nomeArquivo: string | null;
  competenciaRef: number | null;
  bytes: number | null;
  origem: string;
  disponivelDownload: boolean;
  removidoConvenia: boolean;
  observacao: string | null;
  temCopiaLocal: boolean;
}

const ROTULO_TIPO: Record<string, string> = {
  holerite: "Holerite",
  documento: "Documento",
  recibo_ferias: "Recibo de férias",
  outro: "Outro",
};

export function ArquivosCard({ funcionarioId }: { funcionarioId: string }) {
  const [arquivos, setArquivos] = useState<Arquivo[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<{ arquivos: Arquivo[] }>(`/funcionarios/${funcionarioId}/arquivos`)
      .then((r) => setArquivos(r.arquivos))
      .catch(setErro);
  }, [funcionarioId]);

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!arquivos) return <Carregando linhas={3} />;

  return (
    <Card withBorder shadow="xs" mb="md">
      <Title order={5} mb="sm">
        Arquivos (Convenia) <Badge variant="light">{arquivos.length}</Badge>
      </Title>
      {arquivos.length === 0 ? (
        <Vazio texto="Nenhum arquivo inventariado — rode o sync Convenia (domínio documentos_arquivos) para esta pessoa." />
      ) : (
        <TabelaDados>
          <Table.Thead>
            <Table.Tr>
              <Th>Tipo</Th>
              <Th>Arquivo</Th>
              <Th>Competência</Th>
              <Th>Origem</Th>
              <Th>Situação</Th>
              <Th aria-label="Download" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {arquivos.map((a) => (
              <Table.Tr key={a.id} style={a.removidoConvenia ? { opacity: 0.55 } : undefined}>
                <Table.Td>
                  <Badge variant="outline" color="gray">
                    {ROTULO_TIPO[a.tipo] ?? a.tipo}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{a.nomeArquivo ?? "—"}</Text>
                  {a.observacao && (
                    <Text size="xs" c="dimmed">
                      {a.observacao}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{a.competenciaRef ? fmtCompetencia(a.competenciaRef) : "—"}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {a.origem === "download_solicitacao_ferias" ? "download (recibo de férias)" : "inventário (audit log)"}
                    {a.bytes != null ? ` · ${(a.bytes / 1024).toFixed(0)} KB` : ""}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {a.removidoConvenia ? (
                    <Badge size="sm" color="red" variant="light">
                      removido no Convenia
                    </Badge>
                  ) : a.temCopiaLocal ? (
                    <Badge size="sm" color="sillis" variant="light">
                      cópia local
                    </Badge>
                  ) : (
                    <Tooltip label="Holerites/documentos não têm endpoint de download no token Convenia (pendência com o suporte)" multiline maw={320} withArrow>
                      <Badge size="sm" color="gray" variant="light" style={{ cursor: "help" }}>
                        sem download
                      </Badge>
                    </Tooltip>
                  )}
                </Table.Td>
                <Table.Td>
                  {a.temCopiaLocal && (
                    <ActionIcon
                      component="a"
                      href={urlComToken(`/arquivos/${a.id}/download`)}
                      target="_blank"
                      variant="subtle"
                      aria-label={`Baixar ${a.nomeArquivo ?? "arquivo"}`}
                      size="sm"
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </TabelaDados>
      )}
      <Text size="xs" c="dimmed" mt="sm">
        Inventário somente leitura — a fonte é o Convenia. Só recibos de férias têm cópia local (único acervo baixável no token).
      </Text>
    </Card>
  );
}
